<!--
  ImportWizardStep4 — ขั้นที่ 4 "จับคู่ระดับ (Map Levels) + Suggest with AI" ของ Import Wizard (S12-P1)

  ⚠️ เจ้าของไฟล์นี้คือ Task 5-C — ภายหลัง 6-A เติมบล็อก folder และ 7-A เติมบล็อก tabular ในไฟล์นี้
     (จุดต่อทำคอมเมนต์ไว้ให้แล้วในเทมเพลตและในเมธอด onSuggest/applySuggestion)
     Task อื่นห้ามแก้ไฟล์นี้ (ดู §1.7 Parallel Conflict Zone)

  ── สัญญากับ ImportWizard.vue (ห้ามเปลี่ยนเอง) ───────────────────────────────
  props
    state      Object  (required)  อ็อบเจ็กต์สถานะกลางที่ views/GraphEditor.vue เป็นเจ้าของ (มติ D10)
                                   → อ่านอย่างเดียว ห้าม mutate ห้าม reassign
    graphKey   String  (required)
    canEdit    Boolean (false)
    mobile     Boolean (false)     = $vuetify.display.smAndDown ที่ parent คำนวณมาให้แล้ว

  emits
    patch   (fields: Object)   ทางเดียวที่เปลี่ยน state ได้
    notify  ({ text, colour })
    goto    (step: Number)     ปุ่มลัดเท่านั้น — ปุ่ม Back/Next ปกติ parent เป็นคน render

  ฟิลด์ของ state ที่ขั้นนี้ "เขียนได้" (ผ่าน patch)
    levelMapping (Array<{ level, nodeType }>) · aiFilledLevels (Array<string> ช่องที่ AI เติมให้)
    folderNodeTypes · folderRelationMode · folderRelationsByLevel · folderRelations   (6-A)
    step4Busy (Boolean) · step4Blocked (Boolean) · step4BlockReason (String ที่ $t แล้ว)

  🔴 ขั้นนี้ "ห้ามบล็อก" เด็ดขาด — AI ล้ม / กราฟไม่มีชนิดข้อมูล ก็ยังกด "ถัดไป" ได้ (TC-51 · TC-52)
     ⇒ ไฟล์นี้จึงไม่มีที่ไหนเลยที่ patch({ step4Blocked: true }) · step4Busy ต้องถูกล้างทุกเส้นทาง

  🔴 ตัวเลือกชนิดข้อมูล = union ตามต้นทาง nexa `getMdNodeTypes()` (index.html:19257–19270)
     ชนิดจาก GraphSchemaType ของกราฟนั้น (typeKind === 'node') "ก่อน" แล้วต่อท้ายด้วยชนิดมาตรฐาน 14 ตัว
     (MD_NODE_TYPES · index.html:19141–19145) · dedup สตริงตรงตัวโดยคงลำดับ · ลิสต์นี้ไม่มีทางว่าง
     และ "— ไม่สร้างโหนด —" เป็นตัวเลือกแรกเสมอ (typeOptionHtmlSkippable · index.html:19276)

  levelMapping ที่ส่งขึ้น state มีครบทุกระดับที่โชว์บนจอ — ระดับที่ผู้ใช้เลือก "ไม่สร้างโหนด" ส่งเป็น
  nodeType: '' ซึ่ง HeadingNodeBuilder.TypeForLevel ฝั่ง backend ตีความว่า "ข้ามระดับนั้น" อยู่แล้ว
  (ระดับที่ไม่ได้ส่งไปเลยต่างหากที่ backend เติมชนิดมาตรฐานให้)

  testid ของขั้นนี้ต้องขึ้นต้น wiz- เท่านั้น — ห้ามใช้ wizard- และห้ามใช้ index ของ array
  (ใช้ wiz-level-sel-file · wiz-level-sel-1 … wiz-level-sel-6 ซึ่งมาจาก "เลขระดับ" ไม่ใช่ลำดับในอาร์เรย์)
-->
<template>
  <div data-testid="wiz-step4">
    <div class="wiz-section">{{ $t('graphRagWizMapIntro') }}</div>
    <!--
      ✏️ 2026-10-21 (ผู้ใช้ขอ) ปุ่มให้ AI แนะนำ + คำอธิบายชนิดมาตรฐาน แสดง "เฉพาะตอนที่ใช้ได้จริง" (suggestAvailable)
      เดิมโชว์เป็นปุ่มกดไม่ได้ เช่นกราฟยังไม่มีชนิดข้อมูลใน Ontology — backend คัดผลเหลือเฉพาะชนิดที่กราฟมี จึงกดไปก็ไม่ได้อะไร
    -->
    <template v-if="suggestAvailable">
      <!-- เลือกชนิดมาตรฐานที่กราฟยังไม่มีได้ แต่ต้องรู้ล่วงหน้าว่ามันจะถูกรายงานเป็น unknownTypes ตอนให้ AI แนะนำ -->
      <div class="wiz-sub mt-1" data-testid="wiz-std-type-hint">{{ $t('graphRagWizStdTypeHint') }}</div>

      <div class="wiz-actions mt-3" :class="{ 'wiz-actions--stack': mobile }">
        <div class="wiz-action-slot">
          <v-btn
            color="#C32E0E"
            variant="flat"
            :block="mobile"
            :loading="busy"
            :disabled="suggestDisabled"
            prepend-icon="mdi-creation"
            data-testid="wiz-suggest-ai"
            @click="onSuggest"
          >
            {{ $t('graphRagWizSuggestAi') }}
          </v-btn>
        </div>
      </div>
    </template>

    <!-- ── ผลจาก AI: สำเร็จ / ล้ม / คำเตือนจาก backend / ชนิดที่กราฟไม่มี ── -->
    <div v-if="suggestedCount" class="wiz-suggest-status mt-2" aria-live="polite" data-testid="wiz-suggest-status">
      <v-icon size="16" color="#C32E0E">mdi-creation</v-icon>
      <span>{{ $t('graphRagWizSuggestDone', { count: suggestedCount }) }}</span>
    </div>

    <v-alert v-if="suggestFailed" type="warning" variant="tonal" density="compact" class="mt-3" aria-live="polite" data-testid="wiz-step4-error">
      {{ $t('graphRagWizSuggestFailed') }}
    </v-alert>

    <!-- warning ของ backend เป็นข้อความสำเร็จรูปที่อธิบายว่าทำไมผลไม่ครบ (เช่น กราฟไม่มีชนิดข้อมูล / โมเดลตอบไม่ได้) -->
    <v-alert v-if="backendWarning" type="warning" variant="tonal" density="compact" class="mt-3" aria-live="polite" data-testid="wiz-step4-warning">
      {{ backendWarning }}
    </v-alert>

    <v-alert v-if="unknownTypes.length" type="info" color="#C32E0E" variant="tonal" density="compact" class="mt-3" aria-live="polite" data-testid="wiz-unknown-types">
      {{ $t('graphRagWizUnknownTypes', { types: unknownTypes.join(', ') }) }}
    </v-alert>

    <!-- ✏️ 2026-10-21 ตัดกล่อง "กราฟยังไม่มีชนิดข้อมูลของตัวเอง" ออก (ผู้ใช้ขอ — ไม่มีปุ่มให้ทำอะไรต่อ) · ตารางด้านล่างใช้ชนิดมาตรฐานได้ตามปกติ -->

    <!--
      ── โหมด Folder (6-A) ─────────────────────────────────────────────────────
      ตาม wireframe บล็อกของโฟลเดอร์อยู่ "เหนือ" ตารางระดับเสมอ — ตารางระดับด้านล่างใช้ร่วมกันทั้งสองโหมด
      (ในโหมดโฟลเดอร์ ตารางระดับคือส่วน 📄 File Root & Headings ของต้นทาง nexa)
    -->
    <template v-if="folderMode">
      <!-- 📁 ชนิดข้อมูลของแต่ละโฟลเดอร์ — เยื้องตามความลึก · ตัวเลือกแรกคือ "ไม่สร้างโหนด" -->
      <div class="mt-4" data-testid="wiz-folder-types">
        <div class="wiz-folder-head">
          <strong class="wiz-folder-title">📁 {{ $t('graphRagWizFolderTypes') }}</strong>
        </div>
        <div class="wiz-sub">{{ $t('graphRagWizFolderSkipHint') }}</div>

        <div v-if="!folderRows.length" class="wiz-sub mt-2" data-testid="wiz-folder-types-empty">{{ $t('graphRagWizNoSubFolders') }}</div>

        <v-row v-for="row in folderRows" :key="row.key" dense align="center" class="wiz-level-row">
          <v-col cols="12" md="5">
            <div class="wiz-level-label wiz-folder-name" :style="{ paddingInlineStart: row.depth * 14 + 'px' }" :title="row.path">📁 {{ row.name }}</div>
          </v-col>

          <v-col cols="12" md="7">
            <v-tooltip :disabled="canEdit" :text="$t('graphRagReadOnly')" location="top">
              <template #activator="{ props }">
                <div v-bind="props">
                  <v-select
                    :model-value="row.value"
                    :items="typeItems"
                    item-title="label"
                    item-value="typeId"
                    :aria-label="row.path"
                    :class="{ 'wiz-ai-filled': row.aiFilled }"
                    :disabled="!canEdit"
                    density="comfortable"
                    variant="outlined"
                    hide-details
                    :data-testid="'wiz-folder-sel-' + row.key"
                    @update:menu="onMenuToggle"
                    @update:model-value="onFolderTypeChange(row.path, $event)"
                  >
                    <!-- จุดสีหลังชื่อชนิด = สีที่โหนดชนิดนี้จะได้บนผืนกราฟ (typeColor) -->
                    <template #selection="{ item }">
                      <span class="wiz-type-sel">{{ item.title }}<span v-if="item.raw.typeId" class="wiz-type-dot" :style="{ background: typeColor(item.raw.typeId) }" /></span>
                    </template>
                    <template #item="{ props: itemProps, item }">
                      <v-list-item v-bind="itemProps">
                        <template v-if="item.raw.typeId" #append><span class="wiz-type-dot" :style="{ background: optionColor('folder:' + row.key, item.raw.typeId) }" /></template>
                      </v-list-item>
                    </template>
                    <template #append-inner>
                      <v-icon v-if="row.aiFilled" size="16" color="#C32E0E">mdi-creation</v-icon>
                    </template>
                  </v-select>
                </div>
              </template>
            </v-tooltip>
          </v-col>
        </v-row>
      </div>

      <!-- 🔗 ความสัมพันธ์ของเส้นที่โฟลเดอร์ลากไปหาแม่ — ตามระดับ (ค่าเริ่มต้น) หรือรายโฟลเดอร์ -->
      <div class="mt-4" data-testid="wiz-folder-relations">
        <div class="wiz-folder-head">
          <strong class="wiz-folder-title">🔗 {{ $t('graphRagWizFolderRelations') }}</strong>

          <v-btn-toggle :model-value="relationMode" mandatory density="compact" color="#C32E0E" :disabled="!canEdit" data-testid="wiz-folder-rel-mode">
            <v-btn value="level" size="small" data-testid="wiz-folder-rel-mode-level" @click="setRelationMode('level')">{{ $t('graphRagWizRelByLevel') }}</v-btn>
            <v-btn value="folder" size="small" data-testid="wiz-folder-rel-mode-folder" @click="setRelationMode('folder')">{{ $t('graphRagWizRelPerFolder') }}</v-btn>
          </v-btn-toggle>
        </div>

        <template v-if="relationMode === 'level'">
          <div v-if="!depthRows.length" class="wiz-sub mt-2" data-testid="wiz-folder-rel-empty">{{ $t('graphRagWizNoSubLevels') }}</div>

          <v-row v-for="row in depthRows" :key="row.key" dense align="center" class="wiz-level-row">
            <v-col cols="12" md="5">
              <div class="wiz-level-label">{{ $t('graphRagWizRelDepthRow', { depth: row.depth }) }}</div>
            </v-col>

            <v-col cols="12" md="7">
              <v-select
                :model-value="row.value"
                :items="relationItems"
                item-title="label"
                item-value="typeId"
                :aria-label="$t('graphRagWizRelDepthRow', { depth: row.depth })"
                :disabled="!canEdit"
                density="comfortable"
                variant="outlined"
                hide-details
                :data-testid="'wiz-folder-rel-level-' + row.depth"
                @update:model-value="onDepthRelationChange(row.depth, $event)"
              />
            </v-col>
          </v-row>
        </template>

        <template v-else>
          <div v-if="!subFolderRows.length" class="wiz-sub mt-2" data-testid="wiz-folder-rel-empty">{{ $t('graphRagWizNoSubFolders') }}</div>

          <v-row v-for="row in subFolderRows" :key="row.key" dense align="center" class="wiz-level-row">
            <v-col cols="12" md="5">
              <div class="wiz-level-label wiz-folder-name" :style="{ paddingInlineStart: row.depth * 14 + 'px' }" :title="row.path">📁 {{ row.name }}</div>
            </v-col>

            <v-col cols="12" md="7">
              <v-select
                :model-value="row.relation"
                :items="relationItems"
                item-title="label"
                item-value="typeId"
                :aria-label="row.path"
                :disabled="!canEdit"
                density="comfortable"
                variant="outlined"
                hide-details
                :data-testid="'wiz-folder-rel-path-' + row.key"
                @update:model-value="onPathRelationChange(row.path, $event)"
              />
            </v-col>
          </v-row>
        </template>
      </div>
    </template>

    <!-- ── ตารางจับคู่ระดับ (ฟอร์ม — ห้ามใช้ v-data-table) ───────────── -->
    <div class="mt-4" data-testid="wiz-level-rows">
      <v-skeleton-loader v-if="busy" type="list-item-two-line@3" data-testid="wiz-level-rows-loading" />

      <template v-else>
        <v-row v-for="row in levelRows" :key="row.key" dense align="center" class="wiz-level-row">
          <v-col cols="12" md="5">
            <div class="wiz-level-label">{{ row.label }}</div>
          </v-col>

          <v-col cols="12" md="7">
            <v-tooltip :disabled="canEdit" :text="$t('graphRagReadOnly')" location="top">
              <template #activator="{ props }">
                <div v-bind="props">
                  <v-select
                    :model-value="row.value"
                    :items="typeItems"
                    item-title="label"
                    item-value="typeId"
                    :aria-label="row.label"
                    :class="{ 'wiz-ai-filled': row.aiFilled }"
                    :disabled="!canEdit"
                    density="comfortable"
                    variant="outlined"
                    hide-details
                    :data-testid="'wiz-level-sel-' + row.key"
                    @update:menu="onMenuToggle"
                    @update:model-value="onLevelChange(row.level, $event)"
                  >
                    <!-- จุดสีหลังชื่อชนิด = สีที่โหนดชนิดนี้จะได้บนผืนกราฟ (typeColor) -->
                    <template #selection="{ item }">
                      <span class="wiz-type-sel">{{ item.title }}<span v-if="item.raw.typeId" class="wiz-type-dot" :style="{ background: typeColor(item.raw.typeId) }" /></span>
                    </template>
                    <template #item="{ props: itemProps, item }">
                      <v-list-item v-bind="itemProps">
                        <template v-if="item.raw.typeId" #append><span class="wiz-type-dot" :style="{ background: optionColor('level:' + row.key, item.raw.typeId) }" /></template>
                      </v-list-item>
                    </template>
                    <template #append-inner>
                      <v-icon v-if="row.aiFilled" size="16" color="#C32E0E">mdi-creation</v-icon>
                    </template>
                  </v-select>
                </div>
              </template>
            </v-tooltip>

            <div v-if="row.showSkipHint" class="wiz-sub mt-1">{{ $t('graphRagWizSkipLevelHint') }}</div>
          </v-col>
        </v-row>
      </template>
    </div>

    <!--
      ── ไฟล์ตาราง (7-A) ───────────────────────────────────────────────────────
      ตาม wireframe บล็อกนี้อยู่ "ใต้" ตารางระดับ
      โหมดตารางทั่วไป → 📊 กำหนดชนิดข้อมูลของ ทั้งไฟล์ / ทั้งชีต / แต่ละแถว
      โหมดตารางแบบกำหนดเอง → 📐 แผงจับคู่คอลัมน์รายชีต (ยก StructuredImportWizard.vue มาใช้ ไม่เขียนใหม่)
    -->
    <div v-if="standardTabularFiles.length" class="mt-4" data-testid="wiz-tabular-type-rows">
      <div class="wiz-folder-head">
        <strong class="wiz-folder-title">📊 {{ $t('graphRagWizTabularTypes') }}</strong>
      </div>

      <v-row v-for="row in tabularRows" :key="row.key" dense align="center" class="wiz-level-row">
        <v-col cols="12" md="5">
          <div class="wiz-level-label">{{ row.label }}</div>
        </v-col>

        <v-col cols="12" md="7">
          <v-tooltip :disabled="canEdit" :text="$t('graphRagReadOnly')" location="top">
            <template #activator="{ props }">
              <div v-bind="props">
                <v-select
                  :model-value="row.value"
                  :items="typeItems"
                  item-title="label"
                  item-value="typeId"
                  :aria-label="row.label"
                  :disabled="!canEdit"
                  density="comfortable"
                  variant="outlined"
                  hide-details
                  :data-testid="'wiz-tabular-sel-' + row.key"
                  @update:menu="onMenuToggle"
                  @update:model-value="onTabularTypeChange(row.key, $event)"
                >
                  <!-- จุดสีหลังชื่อชนิด = สีที่โหนดชนิดนี้จะได้บนผืนกราฟ (typeColor) -->
                  <template #selection="{ item }">
                    <span class="wiz-type-sel">{{ item.title }}<span v-if="item.raw.typeId" class="wiz-type-dot" :style="{ background: typeColor(item.raw.typeId) }" /></span>
                  </template>
                  <template #item="{ props: itemProps, item }">
                    <v-list-item v-bind="itemProps">
                      <template v-if="item.raw.typeId" #append><span class="wiz-type-dot" :style="{ background: optionColor('tabular:' + row.key, item.raw.typeId) }" /></template>
                    </v-list-item>
                  </template>
                </v-select>
              </div>
            </template>
          </v-tooltip>
        </v-col>
      </v-row>
    </div>

    <div v-if="structuredSheetRows.length" class="mt-4" data-testid="wiz-structured-panel">
      <div class="wiz-folder-head">
        <strong class="wiz-folder-title">📐 {{ $t('graphRagWizStructuredPanel') }}</strong>
      </div>

      <v-row v-for="row in structuredSheetRows" :key="row.key" dense align="center" class="wiz-level-row">
        <v-col cols="12" md="7">
          <div class="wiz-level-label wiz-folder-name" :title="row.fileName">{{ row.fileName }} · {{ row.label || row.sheetName }}</div>
          <div class="wiz-sub">{{ sheetStatusText(row) }}</div>
        </v-col>

        <v-col cols="12" md="5">
          <v-btn variant="outlined" :block="mobile" :disabled="!canEdit" prepend-icon="mdi-table-cog" :data-testid="'wiz-structured-config-' + row.key" @click="openStructured(row)">
            {{ $t('graphRagWizConfigureColumns') }}
          </v-btn>
        </v-col>
      </v-row>

      <!-- dialog เดิมของการนำเข้าตาราง — เปิดแบบ configureOnly จึงคืนค่าการจับคู่ให้ wizard แทนการนำเข้าทันที -->
      <StructuredImportWizard
        v-model="structuredOpen"
        configure-only
        :graph-key="graphKey"
        :session-id="structuredSessionId"
        :initial-sheet="structuredSheetName"
        :can-edit="canEdit"
        @configured="onStructuredConfigured"
        @notify="$emit('notify', $event)"
      />
    </div>
  </div>
</template>

<script>
import StructuredImportWizard from '@/components/GraphRAG/StructuredImportWizard.vue';

/** ระดับ 0 = โหนดหลักของไฟล์ · 1–6 = หัวข้อ H1–H6 (ตรงกับ RequestLevelMapping.level ฝั่ง backend) */
const CONST_FILE_LEVEL = 0;
const CONST_MAX_HEADING_LEVEL = 6;

/** ระดับที่ให้ตั้งค่าได้เมื่ออ่านเนื้อหาไฟล์ไม่ได้เลย (เช่นเปิดค้างไว้ข้ามรอบจนไม่มี markdown ในมือ) */
const CONST_FALLBACK_LEVELS = [1, 2, 3];

/** ATX heading ที่ต้องมีเว้นวรรคหลัง # เสมอ — กัน #hashtag เหมือน MarkdownChunker.IsAtxHeadingLine ฝั่ง backend */
const CONST_ATX_HEADING = /^(#{1,6})\s+\S/;

/**
 * ชนิดข้อมูลมาตรฐานที่ต่อท้ายชนิดของกราฟเสมอ (ต้นทาง MD_NODE_TYPES · nexa index.html:19141–19145)
 * มีไว้เพื่อให้ dropdown ไม่มีทางว่าง แม้กราฟยังไม่ได้ตั้ง GraphSchemaType เลยสักตัว
 */
/**
 * สีชนิดโหนดแบบเดียวกับ backend `GraphRagNodeTypeColors` (BUILTIN_NODE_TYPE_DEFAULTS + palette ของ nexa schema.rs)
 * ใช้ทำนายสีของชนิด "ใหม่" ที่กราฟยังไม่เคยมี — ชนิดที่มีอยู่แล้วใช้สีจริงจาก GetTypeColors เสมอ · แก้ฝั่งหนึ่งต้องแก้อีกฝั่งด้วย
 */
const CONST_BUILTIN_TYPE_COLORS = {
  GraphNode: '#F6C177',
  Heading1: '#E07B54',
  Heading2: '#E8B86D',
  Heading3: '#67C3B0',
  Heading4: '#9B8EC4',
  Heading5: '#7FB4D4',
  Heading6: '#B8D8A4',
};
const CONST_AUTO_PALETTE = ['#FF6B6B', '#44AF69', '#4D96FF', '#F38181', '#AA96DA', '#FCBAD3', '#A8D8EA', '#FFE66D', '#95E1D3', '#FFD93D'];
/** GraphRagSettingsBal.CONST_DEFAULT_COLOR — ชนิดใน Ontology ที่ไม่ได้ตั้งสี */
const CONST_DEFAULT_TYPE_COLOR = '#aaaaaa';

/**
 * ชนิดมาตรฐานที่ต่อท้ายชนิดของกราฟ (Ontology) ในทุกช่องของขั้นนี้
 * ✏️ 2026-10-21 (ผู้ใช้ขอ) เหลือแค่ GraphNode · Product · Heading1–6 — ตัด Category / ProductType / Service / Promotion /
 * Feature / Condition / Troubleshooting ของ nexa ออก (ชนิดใน Ontology ของกราฟยังขึ้นก่อนเหมือนเดิม)
 */
const CONST_MD_NODE_TYPES = ['GraphNode', 'Product', 'Heading1', 'Heading2', 'Heading3', 'Heading4', 'Heading5', 'Heading6'];

/** ค่าเริ่มต้นของแต่ละระดับ (ต้นทาง MD_LEVEL_DEFAULTS · nexa index.html:19146) — ระดับ 0 = Product */
const CONST_MD_LEVEL_DEFAULTS = { 0: 'Product', 1: 'Heading1', 2: 'Heading2', 3: 'Heading3', 4: 'Heading4', 5: 'Heading5', 6: 'Heading6' };

/**
 * 6-A — ค่าเริ่มต้นของชนิดโหนดตามความลึกโฟลเดอร์ (ต้นทาง FOLDER_DEPTH_DEFAULTS · nexa index.html:19147)
 * ชื่อพวกนี้อยู่ใน union ของตัวเลือกเสมอ (CONST_MD_NODE_TYPES) จึงตั้งเป็นค่าเริ่มต้นได้ตรง ๆ
 */
// ✏️ 2026-10-21 Category / ProductType ไม่อยู่ในรายการแล้ว ⇒ โฟลเดอร์สองชั้นบนใช้ GraphNode (ชนิดกลางของระบบ) แทน
const CONST_FOLDER_DEPTH_DEFAULTS = { 0: 'GraphNode', 1: 'GraphNode', 2: 'Product', 3: 'Heading1', 4: 'Heading2', 5: 'Heading3' };

/** ใช้เมื่อกราฟยังไม่มี GraphSchemaType ชนิด relation — ชุดเดียวกับต้นทาง nexa (suggestFolderTypes) */
const CONST_FALLBACK_RELATIONS = ['belongs_to_section', 'contains_section', 'has_feature', 'contains', 'relates_to', 'suggests'];

/** ค่าเริ่มต้นของทุกแถวความสัมพันธ์ตาม §Folder Mode */
const CONST_DEFAULT_RELATION = 'belongs_to_section';

/** โหมดความสัมพันธ์ที่ backend เก็บลง RequestJson — มีสองค่าเท่านั้น */
const CONST_REL_MODE_LEVEL = 'level';
const CONST_REL_MODE_FOLDER = 'folder';

/** โหมดตารางรองรับสองนามสกุลนี้เท่านั้น (7-A) */
const CONST_TABULAR_EXT = ['.csv', '.xlsx'];

/**
 * 7-A — ค่าเริ่มต้นของชนิดโหนดในโหมดตารางทั่วไป (ต้นทาง buildXlsxTypeRows ของ nexa)
 * เหมือนโฟลเดอร์: อยู่ใน union ของตัวเลือกเสมอ จึงตั้งตรง ๆ ได้
 */
// ✏️ 2026-10-21 Category ไม่อยู่ในรายการแล้ว ⇒ ทั้งไฟล์ / ทั้งชีตใช้ GraphNode แทน
const CONST_TABULAR_DEFAULTS = { file: 'GraphNode', sheet: 'GraphNode', row: 'Product' };

/** ลำดับแถวของบล็อกตาราง — ทั้งไฟล์ → ทั้งชีต → แต่ละแถว */
const CONST_TABULAR_SCOPES = ['file', 'sheet', 'row'];

export default {
  name: 'ImportWizardStep4',
  components: { StructuredImportWizard },
  props: {
    state: { type: Object, required: true },
    graphKey: { type: String, required: true },
    canEdit: { type: Boolean, default: false },
    mobile: { type: Boolean, default: false },
  },
  emits: ['patch', 'notify', 'goto'],
  data() {
    return {
      /** GraphSchemaType ชนิด node ของกราฟนี้ — โหลดใหม่ทุกครั้งที่เข้าขั้น 4 (TC-52) */
      nodeTypes: [],
      // { [typeId]: '#hex' } สีที่ผืนกราฟใช้อยู่ตอนนี้ (GetTypeColors) — ฐานของ typeColorPlan
      graphTypeColors: {},
      /** GraphSchemaType ชนิด relation — ใช้เป็นตัวเลือกของแผงความสัมพันธ์โฟลเดอร์ (6-A) */
      relationTypes: [],
      /** path ของโฟลเดอร์ที่ AI เพิ่งเติมให้ — ใช้แค่โชว์ ✨ ไม่ต้องเก็บลง state กลาง */
      aiFilledFolders: [],
      loadingSchema: false,
      /** ผลรอบล่าสุดของปุ่มให้ AI แนะนำ */
      suggestedCount: 0,
      suggestFailed: false,
      backendWarning: '',
      unknownTypes: [],
      /** ระดับที่ AI เสนอมาแต่เอกสารตรวจไม่เจอ — ต้องโชว์แถวให้ด้วย ไม่งั้นค่าที่เติมจะมองไม่เห็น */
      suggestedLevels: [],
      /** 7-A — dialog ตั้งค่าคอลัมน์ของชีตที่กำลังแก้อยู่ (ยก StructuredImportWizard เดิมมาใช้) */
      structuredOpen: false,
      structuredKey: '',
      /** เขียนค่าเริ่มต้นต่อระดับลง state ได้ก็ต่อเมื่อผู้ใช้เดินมาถึงขั้นนี้แล้วเท่านั้น */
      enteredStep4: false,
    };
  },
  computed: {
    busy() {
      return !!this.state.step4Busy;
    },
    /** กราฟนี้ตั้ง GraphSchemaType ชนิด node ไว้เองหรือยัง — ไม่เกี่ยวกับว่า dropdown มีตัวเลือกไหม */
    hasOntologyTypes() {
      return this.nodeTypes.length > 0;
    },
    /** ไฟล์ที่แปลงเสร็จแล้วเท่านั้นที่มีเนื้อหาให้ดูระดับหัวข้อและมี session ให้ AI อ่าน */
    doneFiles() {
      return (this.state.files || []).filter((f) => f.status === 'done' && f.session);
    },
    sessionIds() {
      return this.doneFiles.map((f) => f.session.sessionId).filter(Boolean);
    },
    /**
     * ตัวเลือกแรกเสมอคือ "ไม่สร้างโหนด" (ค่าว่าง) — typeOptionHtmlSkippable · nexa index.html:19276
     * ที่เหลือคือ union ตาม getMdNodeTypes (nexa index.html:19257–19270): ชนิดของกราฟก่อน แล้วต่อท้ายด้วย
     * ชนิดมาตรฐาน · dedup ด้วยสตริงตรงตัวหลัง trim โดยคงลำดับเดิม ⇒ ลิสต์นี้ไม่มีทางว่าง
     * subtitle บอกที่มาของแต่ละตัวเลือก (Vuetify 3.8 ไม่มีหัวข้อกลุ่มใน v-select — VSelect เรนเดอร์ VListItem ตรง ๆ)
     */
    typeItems() {
      const items = [{ label: this.$t('graphRagWizSkipLevel'), typeId: '' }];
      const seen = {};

      this.nodeTypes.forEach((t) => {
        const typeId = String((t && t.typeId) || '').trim();

        if (!typeId || seen[typeId]) return;

        seen[typeId] = true;
        items.push({ label: t.label || typeId, typeId: typeId, props: { subtitle: this.$t('graphRagWizTypeFromGraph') } });
      });

      CONST_MD_NODE_TYPES.forEach((typeId) => {
        if (seen[typeId]) return;

        seen[typeId] = true;
        items.push({ label: typeId, typeId: typeId, props: { subtitle: this.$t('graphRagWizTypeStandard') } });
      });

      return items;
    },
    /**
     * สีที่แต่ละชนิดจะได้บนผืนกราฟหลังนำเข้า — จำลอง GraphRagNodeTypeColors.Build:
     * ชนิดที่กราฟมีสีอยู่แล้ว (Ontology / ใช้อยู่) ใช้สีนั้น · GraphNode / Heading1–6 ใช้สี builtin
     * ชนิดใหม่อื่น ๆ ได้สีถัดไปใน palette ตามลำดับที่จะถูกสร้าง (โฟลเดอร์ → ระดับในไฟล์ → ตาราง)
     * next = ช่องถัดไปของ palette สำหรับชนิดใหม่ที่ยังไม่ได้เลือก (ตัวเลือกใน dropdown)
     */
    typeColorPlan() {
      return this.buildColorPlan(null, null);
    },
    /** แถวที่สร้างโหนด เรียงตามลำดับที่จะถูกสร้าง (โฟลเดอร์ → ระดับในไฟล์ → ตาราง) — uid ไม่ซ้ำข้ามบล็อก */
    colorRows() {
      return [
        ...(this.folderRows || []).map((row) => ({ uid: 'folder:' + row.key, value: row.value })),
        ...(this.levelRows || []).map((row) => ({ uid: 'level:' + row.key, value: row.value })),
        ...(this.tabularRows || []).map((row) => ({ uid: 'tabular:' + row.key, value: row.value })),
      ];
    },
    /** ชนิดที่เลือกได้จริงบนจอ — ใช้กรองค่าที่ AI เสนอ ไม่ให้หลุดค่าที่ไม่มีในลิสต์ */
    selectableTypeIds() {
      const known = {};

      this.typeItems.forEach((item) => {
        if (item.typeId) known[item.typeId] = true;
      });

      return known;
    },
    /** levelMapping ของ state → map ไว้ค้นเร็ว ๆ ต่อแถว */
    mappingByLevel() {
      const map = {};

      (this.state.levelMapping || []).forEach((m) => {
        if (m && typeof m.level === 'number') map[m.level] = m.nodeType || '';
      });

      return map;
    },
    /** ระดับหัวข้อที่ "มีจริง" ในไฟล์ที่แปลงแล้ว (นอกบล็อกโค้ด) */
    documentLevels() {
      const found = new Set();

      this.doneFiles.forEach((f) => {
        this.headingLevelsOf(this.markdownOf(f)).forEach((level) => found.add(level));
      });

      return [...found].sort((a, b) => a - b);
    },
    /** มีเนื้อหาให้ตรวจระดับหัวข้อจริงไหม — ไม่มี = ตรวจไม่ได้ ไม่ใช่ "ไม่มีหัวข้อ" */
    hasMarkdownSource() {
      return this.doneFiles.some((f) => !!this.markdownOf(f));
    },
    /** แถวของตาราง: โหนดหลักของไฟล์ + ระดับที่เอกสารมีจริง (รวมระดับที่ AI เสนอเพิ่ม) */
    levelRows() {
      const levels = new Set([CONST_FILE_LEVEL]);
      const detected = this.documentLevels;
      // อ่านเนื้อหาไม่ได้ = ไม่รู้ว่ามีระดับไหน จึงเปิดช่องพื้นฐานไว้ให้ตั้งเอง
      // ส่วนเอกสารที่อ่านได้แต่ไม่มีหัวข้อเลย ต้องเหลือเฉพาะแถวโหนดหลักของไฟล์
      const base = detected.length || this.hasMarkdownSource ? detected : CONST_FALLBACK_LEVELS;

      base.forEach((l) => levels.add(l));
      this.suggestedLevels.forEach((l) => levels.add(l));

      const aiFilled = this.state.aiFilledLevels || [];

      return [...levels]
        .filter((level) => level >= CONST_FILE_LEVEL && level <= CONST_MAX_HEADING_LEVEL)
        .sort((a, b) => a - b)
        .map((level) => {
          const key = this.levelKey(level);
          const value = this.levelTypeOf(level);

          return {
            level: level,
            key: key,
            value: value,
            label: level === CONST_FILE_LEVEL ? this.$t('graphRagWizLevelFile', { count: this.doneFiles.length }) : this.$t('graphRagWizLevelHeading', { n: level }),
            aiFilled: !!value && aiFilled.indexOf(key) >= 0,
            showSkipHint: !value && level !== CONST_FILE_LEVEL,
          };
        });
    },
    /** ลายเซ็นของชุดระดับที่โชว์อยู่ — ใช้จุด watcher แทนการ watch อาร์เรย์ที่สร้างใหม่ทุกรอบ */
    levelSignature() {
      return this.levelRows.map((row) => row.key).join('|');
    },
    // ── ไฟล์ตาราง (7-A) ──────────────────────────────────────────────────────
    tabularFiles() {
      return this.doneFiles.filter((f) => CONST_TABULAR_EXT.includes(f.ext));
    },
    standardTabularFiles() {
      return this.tabularFiles.filter((f) => f.tableMode === 'standard');
    },
    structuredTabularFiles() {
      return this.tabularFiles.filter((f) => f.tableMode === 'structured');
    },
    /** ชีตของไฟล์ตารางแบบกำหนดเองที่ยังอยู่ในคิวจริง — คีย์มาจากชื่อไฟล์+ชีต ไม่ใช่ index */
    structuredSheetRows() {
      const keys = this.structuredTabularFiles.map((f) => f.key);

      return (this.state.structuredSheets || []).filter((row) => keys.indexOf(row.fileKey) >= 0 && row.include !== false);
    },
    structuredRow() {
      return this.structuredSheetRows.find((row) => row.key === this.structuredKey) || null;
    },
    structuredSessionId() {
      return this.structuredRow ? this.structuredRow.sessionId : null;
    },
    structuredSheetName() {
      return this.structuredRow ? this.structuredRow.sheetName : null;
    },
    tabularTypes() {
      return this.state.tabularTypes || {};
    },
    /** 3 แถวของโหมดตารางทั่วไป — ค่าเริ่มต้นแมปเข้ากับชนิดข้อมูลที่กราฟมีจริง ไม่มีตรงเลย = ว่าง */
    tabularRows() {
      return CONST_TABULAR_SCOPES.map((scope) => ({
        key: scope,
        label: this.tabularScopeLabel(scope),
        value: this.tabularTypeOf(scope),
      }));
    },
    // ── โหมด Folder (6-A) ────────────────────────────────────────────────────
    folderMode() {
      return this.state.mode === 'folder';
    },
    /** ทุกโฟลเดอร์ที่ปรากฏในคิว รวมบรรพบุรุษทุกชั้น — เรียงตามลำดับที่พบ · คีย์มาจาก path ไม่ใช่ index */
    folderPaths() {
      if (!this.folderMode) return [];

      const seen = {};
      const rows = [];

      (this.state.files || []).forEach((file) => {
        const path = file.folderPath || '';
        if (!path) return;

        const parts = path.split('/');

        parts.forEach((name, depth) => {
          const sub = parts.slice(0, depth + 1).join('/');
          if (seen[sub]) return;

          seen[sub] = true;
          rows.push({ key: this.slug(sub), path: sub, name: name, depth: depth });
        });
      });

      return rows;
    },
    /** ลายเซ็นของชุดโฟลเดอร์ — ใช้เป็นตัวจุด watcher แทนการ watch อาร์เรย์ที่สร้างใหม่ทุกรอบ */
    folderSignature() {
      return this.folderPaths.map((row) => row.path).join('|');
    },
    folderTypeByPath() {
      const map = {};

      (this.state.folderNodeTypes || []).forEach((row) => {
        if (row && row.path) map[row.path] = row.nodeType || '';
      });

      return map;
    },
    relationByDepth() {
      const map = {};

      (this.state.folderRelationsByLevel || []).forEach((row) => {
        if (row && typeof row.depth === 'number' && row.relation) map[row.depth] = row.relation;
      });

      return map;
    },
    relationByPath() {
      const map = {};

      (this.state.folderRelations || []).forEach((row) => {
        if (row && row.path && row.relation) map[row.path] = row.relation;
      });

      return map;
    },
    /** แถวของบล็อก 📁 — ค่าที่โชว์ใช้ของ state ก่อน ไม่มีค่อยตกลงมาที่ค่าเริ่มต้นตามความลึก */
    folderRows() {
      return this.folderPaths.map((row) => ({
        key: row.key,
        path: row.path,
        name: row.name,
        depth: row.depth,
        value: this.folderTypeOf(row),
        aiFilled: this.aiFilledFolders.indexOf(row.path) >= 0,
      }));
    },
    /** โฟลเดอร์รากไม่มีแม่ในผัง (แม่ของมันคือ parentNodeKey) ⇒ แถวความสัมพันธ์เริ่มที่ depth 1 */
    subFolderRows() {
      return this.folderPaths
        .filter((row) => row.depth > 0)
        .map((row) => ({ key: row.key, path: row.path, name: row.name, depth: row.depth, relation: this.relationByPath[row.path] || this.defaultRelation }));
    },
    depthRows() {
      const max = this.folderPaths.reduce((found, row) => Math.max(found, row.depth), 0);
      const rows = [];

      for (let depth = 1; depth <= max; depth += 1) {
        rows.push({ key: 'depth-' + depth, depth: depth, value: this.relationByDepth[depth] || this.defaultRelation });
      }

      return rows;
    },
    relationMode() {
      return this.state.folderRelationMode === CONST_REL_MODE_FOLDER ? CONST_REL_MODE_FOLDER : CONST_REL_MODE_LEVEL;
    },
    /** ตัวเลือกมาจาก GraphSchemaType ชนิด relation ของกราฟนั้น · กราฟที่ยังไม่มีใช้ชุดสำรองของ nexa */
    relationItems() {
      const items = this.relationTypes.map((t) => ({ label: t.label || t.typeId, typeId: t.typeId })).filter((t) => !!t.typeId);

      return items.length ? items : CONST_FALLBACK_RELATIONS.map((relation) => ({ label: relation, typeId: relation }));
    },
    defaultRelation() {
      const items = this.relationItems;
      const preferred = items.find((item) => item.typeId === CONST_DEFAULT_RELATION);

      return preferred ? preferred.typeId : items[0].typeId;
    },
    /** ผังที่ส่งให้ backend ตอนกดให้ AI แนะนำ (mode = folder) */
    folderPayload() {
      return this.folderPaths.map((row) => {
        const cut = row.path.lastIndexOf('/');

        return { path: row.path, depth: row.depth, parentPath: cut < 0 ? '' : row.path.slice(0, cut) };
      });
    },
    /**
     * ปุ่มให้ AI แนะนำใช้ได้จริงไหม — ไม่ได้ = ซ่อนปุ่มทั้งก้อน (ไม่โชว์ปุ่มกดไม่ได้)
     * backend คัดผลของ AI ให้เหลือเฉพาะชนิดที่กราฟมีจริง ⇒ กราฟที่ยังไม่มี ontology กดแล้วก็ไม่ได้อะไร
     */
    suggestAvailable() {
      return this.canEdit && this.hasOntologyTypes && this.sessionIds.length > 0 && !!this.state.modelId;
    },
    suggestDisabled() {
      return !this.suggestAvailable || this.busy;
    },
  },
  watch: {
    // v-stepper-window ไม่ทำลาย component เมื่อสลับขั้น ⇒ mounted ยิงครั้งเดียว
    // จึงต้องโหลดชนิดข้อมูลใหม่ทุกครั้งที่ "เข้า" ขั้น 4 เพื่อให้เห็นชนิดที่เพิ่งสร้าง (TC-52)
    'state.step': {
      immediate: true,
      handler(step) {
        if (step !== 4) return;

        this.enteredStep4 = true;
        this.loadSchemaTypes();
        this.syncLevelDefaults();
      },
    },
    graphKey() {
      if (this.state.step === 4) this.loadSchemaTypes();
    },
    /** เพิ่ม/ลบไฟล์แล้วชุดระดับเปลี่ยน ⇒ ระดับที่เพิ่งโผล่ต้องได้ค่าเริ่มต้นของมันเหมือนกัน */
    levelSignature() {
      this.syncLevelDefaults();
    },
    /**
     * คิวเปลี่ยน = ชุดโฟลเดอร์เปลี่ยน ⇒ ต้องเติมค่าเริ่มต้นของแถวใหม่ให้ครบก่อนกดสร้าง (6-A)
     * และผลรอบก่อนของ AI ใช้กับไฟล์ชุดเก่า — ต้องล้างทิ้ง ไม่งั้นจะค้างบอกว่า "แนะนำให้ N ระดับ" ทั้งที่ช่องว่างหมดแล้ว
     */
    folderSignature() {
      this.aiFilledFolders = [];
      this.suggestedCount = 0;
      this.suggestFailed = false;
      this.syncFolderDefaults();
    },
  },
  mounted() {
    // ผู้ใช้กด "จัดการชนิดข้อมูล" แล้วไปสร้างชนิดใหม่ที่แท็บอื่น พอกลับมาต้องเห็นตัวเลือกใหม่ทันที
    // โดยไม่ต้องปิด wizard (TC-52) — wizard ค้างอยู่ขั้น 4 watcher จึงไม่ทำงาน ต้องอาศัยจังหวะแท็บกลับมา
    globalThis.addEventListener('focus', this.onWindowFocus);
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    // เปิด dialog ใหม่ทั้งที่ state ยังค้างอยู่ขั้น 4 → watcher ของ state.step ก็ไม่ทำงานเหมือนกัน
    if (this.state.step === 4) {
      this.enteredStep4 = true;
      this.loadSchemaTypes();
      this.syncLevelDefaults();
    }
  },
  beforeUnmount() {
    globalThis.removeEventListener('focus', this.onWindowFocus);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  },
  methods: {
    /** ทางเดียวที่ขั้นนี้เปลี่ยน state กลางได้ */
    patch(fields) {
      this.$emit('patch', fields);
    },
    notify(text, colour) {
      this.$emit('notify', { text, colour: colour || 'success' });
    },
    levelKey(level) {
      return level === CONST_FILE_LEVEL ? 'file' : String(level);
    },
    /** ตัดอักขระที่ใช้ใน selector ไม่ได้ออก แต่คงตัวอักษร/ตัวเลขทุกภาษาไว้ (ท่าเดียวกับ ImportWizard/GraphEditor) */
    slug(text) {
      return String(text || '')
        .replace(/[^\p{L}\p{N}_-]+/gu, '-')
        .replace(/^-+|-+$/g, '') || 'x';
    },
    /** ขั้น 2 อาจบันทึกเวอร์ชันใหม่ทับไว้ — เอาเนื้อหาที่เลือกอยู่ก่อนเสมอ */
    markdownOf(file) {
      const session = file.session || {};

      return session.selectedMarkdown || session.markdown || '';
    },
    /** ระดับ ATX heading ที่พบในเนื้อหา — ข้ามบล็อกโค้ด ``` / ~~~ เพื่อไม่ให้คอมเมนต์ในโค้ดกลายเป็นหัวข้อ */
    headingLevelsOf(markdown) {
      const levels = [];
      let inFence = false;

      String(markdown || '')
        .split(/\r?\n/)
        .forEach((line) => {
          const text = line.trim();

          if (text.startsWith('```') || text.startsWith('~~~')) {
            inFence = !inFence;

            return;
          }
          if (inFence) return;

          const match = CONST_ATX_HEADING.exec(text);

          if (match) levels.push(match[1].length);
        });

      return levels;
    },
    /** รายชื่อชนิดข้อมูลของกราฟนี้จริง ๆ — ห้าม hardcode (TC-09) */
    loadSchemaTypes() {
      if (!this.graphKey) return;

      this.loadingSchema = true;
      this.$call.GraphRagGetSchemaTypes(
        this.graphKey,
        (res) => {
          this.loadingSchema = false;

          const rows = res && res.status === 0 ? res.data || [] : [];
          this.nodeTypes = rows.filter((t) => t && t.typeKind === 'node');
          this.relationTypes = rows.filter((t) => t && t.typeKind === 'relation');
          this.loadTypeColors();
          // ค่าเริ่มต้นของโฟลเดอร์ต้องแมปกับชนิดที่กราฟมีจริง จึงเติมได้ก็ต่อเมื่อโหลดชนิดข้อมูลเสร็จแล้ว
          this.syncFolderDefaults();
        },
        () => {
          this.loadingSchema = false;
          this.nodeTypes = [];
          this.relationTypes = [];
        }
      );
    },
    /** สีที่ผืนกราฟใช้อยู่ — ล้มก็ไม่เป็นไร จุดสีจะใช้สี builtin / palette แทน */
    loadTypeColors() {
      if (!this.graphKey) return;

      this.$call.GraphRagGetNodeTypeColors(
        this.graphKey,
        (res) => {
          this.graphTypeColors = res && res.status === 0 && res.data ? res.data : {};
        },
        () => {}
      );
    },
    /**
     * จำลอง GraphRagNodeTypeColors.Build ฝั่ง backend
     * overrideUid / overrideType = "ถ้าแถวนี้เลือกชนิดนี้แทน" — ใช้ระบายสีตัวเลือกใน dropdown ของแถวนั้น
     */
    buildColorPlan(overrideUid, overrideType) {
      const colors = { ...this.graphTypeColors };
      const ontology = new Set();

      this.nodeTypes.forEach((t) => {
        const id = String((t && t.typeId) || '').trim();
        if (!id) return;

        ontology.add(id);
        if (!colors[id]) colors[id] = t.color || CONST_DEFAULT_TYPE_COLOR;
      });

      // ช่อง palette ที่ใช้ไปแล้ว = ชนิดใน Ontology + ชนิดที่ได้สีจาก palette (builtin ไม่กิน palette)
      let next = Object.keys(colors).filter((id) => !(CONST_BUILTIN_TYPE_COLORS[id] && !ontology.has(id))).length;
      const assign = (id) => {
        if (!id || colors[id]) return;
        if (CONST_BUILTIN_TYPE_COLORS[id]) {
          colors[id] = CONST_BUILTIN_TYPE_COLORS[id];
          return;
        }

        colors[id] = CONST_AUTO_PALETTE[next % CONST_AUTO_PALETTE.length];
        next += 1;
      };

      this.colorRows.forEach((row) => assign(row.uid === overrideUid ? overrideType : row.value));

      return { colors, next };
    },
    /** สีของตัวเลือกใน dropdown ของแถวหนึ่ง = สีที่จะได้ถ้าแถวนั้นเลือกตัวนี้ (ชนิดใหม่แย่งช่อง palette ของค่าเดิมได้) */
    optionColor(rowUid, typeId) {
      return this.buildColorPlan(rowUid, typeId).colors[typeId] || this.typeColor(typeId);
    },
    /** สีของชนิดนี้บนผืนกราฟ — ชนิดใหม่ที่ยังไม่ได้เลือกที่ไหนเลย = สีถัดไปของ palette (ถ้าเลือกตัวนี้) */
    typeColor(typeId) {
      const plan = this.typeColorPlan;

      return plan.colors[typeId] || CONST_BUILTIN_TYPE_COLORS[typeId] || CONST_AUTO_PALETTE[plan.next % CONST_AUTO_PALETTE.length];
    },
    onWindowFocus() {
      if (this.state.step === 4) this.loadSchemaTypes();
    },
    onVisibilityChange() {
      if (!document.hidden && this.state.step === 4) this.loadSchemaTypes();
    },
    /** เปิดลิสต์ตัวเลือกเมื่อไหร่ = ดึงชนิดข้อมูลล่าสุดตอนนั้น ผู้ใช้จึงไม่มีทางเห็นลิสต์เก่า (TC-52) */
    onMenuToggle(opened) {
      if (opened) this.loadSchemaTypes();
    },
    /**
     * ค่าเริ่มต้นของระดับตาม MD_LEVEL_DEFAULTS (nexa index.html:19146) · ระดับที่เกินตารางใช้ 'Heading'+lvl
     * เหมือน `MD_LEVEL_DEFAULTS[lvl] || ('Heading' + lvl)` ของ _buildLevelRowsFiles (index.html:21277)
     * ค่าที่ได้อยู่ใน union ของตัวเลือกเสมอ จึงตั้งเป็นค่าเริ่มต้นได้ตรง ๆ
     */
    defaultLevelType(level) {
      if (level === CONST_FILE_LEVEL) return CONST_MD_LEVEL_DEFAULTS[CONST_FILE_LEVEL];

      return CONST_MD_LEVEL_DEFAULTS[level] || 'Heading' + String(level);
    },
    /** ค่าที่ state เคยเก็บไว้ชนะเสมอ — รวมกรณีผู้ใช้เลือก "ไม่สร้างโหนด" ที่เก็บเป็นค่าว่าง */
    levelTypeOf(level) {
      const map = this.mappingByLevel;

      return Object.prototype.hasOwnProperty.call(map, level) ? map[level] : this.defaultLevelType(level);
    },
    /**
     * เขียนค่าที่โชว์อยู่ลง state ให้ครบทุกระดับ เพื่อให้สิ่งที่ผู้ใช้เห็นกับสิ่งที่ StartExtract ได้รับตรงกัน
     * เรียกจาก watcher/lifecycle เท่านั้น ห้ามเรียกระหว่าง render · patch เฉพาะตอนค่าต่างจริงเพื่อกัน loop
     */
    syncLevelDefaults() {
      if (!this.enteredStep4 || !this.levelRows.length) return;

      this.patchLevels(this.levelRows.map((row) => ({ level: row.level, nodeType: row.value })));
    },
    patchLevels(mapping, aiFilled) {
      const nextAiFilled = aiFilled || this.state.aiFilledLevels || [];
      const before = JSON.stringify([this.state.levelMapping || [], this.state.aiFilledLevels || []]);
      const after = JSON.stringify([mapping, nextAiFilled]);

      if (before === after) return;

      this.patch({ levelMapping: mapping, aiFilledLevels: nextAiFilled });
    },
    /** ผู้ใช้เลือกเอง = ค่าของผู้ใช้ชนะเสมอ และเครื่องหมาย ✨ ของช่องนั้นต้องหายทันที (TC-08) */
    onLevelChange(level, typeId) {
      const nextType = typeId || '';
      // ระดับที่ตั้งเป็นค่าว่างต้องคงอยู่ในลิสต์ — backend อ่านว่า "ข้ามระดับนั้น" (HeadingNodeBuilder.TypeForLevel)
      // ถ้าตัดทิ้งจะกลายเป็น "ไม่ได้ระบุ" แล้วเด้งกลับไปเป็นค่าเริ่มต้นทันที
      const mapping = this.levelRows.map((row) => ({ level: row.level, nodeType: row.level === level ? nextType : row.value }));

      this.patchLevels(
        mapping,
        (this.state.aiFilledLevels || []).filter((key) => key !== this.levelKey(level))
      );
    },
    onSuggest() {
      if (this.suggestDisabled) return;

      this.suggestedCount = 0;
      this.suggestFailed = false;
      this.backendWarning = '';
      this.unknownTypes = [];
      this.patch({ step4Busy: true });

      const request = {
        graphKey: this.graphKey,
        sessionIds: this.sessionIds,
        modelId: this.state.modelId,
        mode: this.folderMode ? CONST_REL_MODE_FOLDER : 'files',
        // backend ไม่รู้จักโครงโฟลเดอร์ของเครื่องผู้ใช้ — ต้องส่งผังไปพร้อมคำขอเสมอ (TC-44)
        folders: this.folderMode ? this.folderPayload : [],
      };

      this.$call.GraphRagSuggestLevelMapping(
        request,
        (res) => {
          this.patch({ step4Busy: false });

          // degraded ทุกกรณี backend ยังคืน status 0 พร้อม warning — status อื่นคือ validation/สิทธิ์/exception
          if (!res || res.status !== 0) return this.markSuggestFailed();

          this.applySuggestion(res.data || {});
        },
        () => {
          this.patch({ step4Busy: false });
          this.markSuggestFailed();
        }
      );
    },
    /** AI ล้มต้องไม่บล็อกขั้นนี้ — แค่บอกว่าเลือกเองได้ (State Matrix ขั้น 4 · TC-51/52) */
    markSuggestFailed() {
      this.suggestFailed = true;
    },
    applySuggestion(data) {
      this.backendWarning = data.warning || '';
      this.unknownTypes = data.unknownTypes || [];

      const levels = data.levels || [];
      const known = this.selectableTypeIds;
      const mapping = {};

      this.levelRows.forEach((row) => {
        mapping[row.level] = row.value;
      });

      const manualKeys = this.manualLevelKeys();
      const aiFilled = [];
      const seenLevels = [];

      levels.forEach((item) => {
        if (!item || typeof item.level !== 'number') return;

        seenLevels.push(item.level);

        const key = this.levelKey(item.level);
        const suggested = item.suggestedNodeType || '';

        // ค่าที่ไม่มีในลิสต์ตัวเลือกจริง ห้ามเติมลงช่อง ไม่งั้น v-select จะโชว์ค่าที่เลือกกลับไม่ได้
        if (!suggested || !known[suggested]) return;
        // ค่าที่ผู้ใช้ตั้งเองอยู่ก่อนแล้ว ห้ามให้ AI ทับ
        if (manualKeys.indexOf(key) >= 0) return;

        mapping[item.level] = suggested;
        aiFilled.push(key);
      });

      // 6-A: โหมด folder อ่าน data.folders → folderNodeTypes และ data.relationsByLevel → folderRelationsByLevel
      const folderCount = this.applyFolderSuggestion(data);

      this.suggestedLevels = seenLevels;
      this.suggestedCount = aiFilled.length + folderCount;

      this.patchLevels(
        Object.keys(mapping)
          .map((level) => ({ level: Number(level), nodeType: mapping[level] }))
          .sort((a, b) => a.level - b.level),
        aiFilled
      );

      if (!this.suggestedCount && !this.backendWarning && !this.unknownTypes.length) this.markSuggestFailed();
    },
    /**
     * ช่องที่ผู้ใช้ตั้งเอง = ค่าต่างจากค่าเริ่มต้นของระดับนั้น และไม่ได้มาจาก AI รอบก่อน
     * (เกณฑ์เดียวกับ manualFolderPaths — ทุกช่องมีค่าเริ่มต้นติดมาแล้ว จะใช้ "มีค่า = ผู้ใช้ตั้ง" ไม่ได้)
     */
    manualLevelKeys() {
      const aiFilled = this.state.aiFilledLevels || [];

      return this.levelRows.filter((row) => row.value !== this.defaultLevelType(row.level) && aiFilled.indexOf(row.key) < 0).map((row) => row.key);
    },

    // ── ไฟล์ตาราง (7-A) ──────────────────────────────────────────────────────
    tabularScopeLabel(scope) {
      if (scope === 'file') return this.$t('graphRagWizTabularWholeFile');
      if (scope === 'sheet') return this.$t('graphRagWizTabularSheet');

      return this.$t('graphRagWizTabularRow');
    },
    /** ค่าเริ่มต้นของแต่ละขอบเขต — อยู่ใน union ของตัวเลือกเสมอ จึงตั้งตรง ๆ ได้ */
    defaultTabularType(scope) {
      return CONST_TABULAR_DEFAULTS[scope] || '';
    },
    tabularTypeOf(scope) {
      const types = this.tabularTypes;

      return Object.prototype.hasOwnProperty.call(types, scope) && types[scope] ? types[scope] : this.defaultTabularType(scope);
    },
    onTabularTypeChange(scope, typeId) {
      const next = {};

      CONST_TABULAR_SCOPES.forEach((one) => {
        next[one] = one === scope ? typeId || '' : this.tabularTypeOf(one);
      });

      this.patch({ tabularTypes: next });
    },
    sheetStatusText(row) {
      if (row.mapping && row.mapping.nodeKey) return this.$t('graphRagWizSheetConfigured', { column: row.mapping.nodeKey });

      return this.$t('graphRagWizSheetNotConfigured');
    },
    openStructured(row) {
      if (!this.canEdit) return;

      this.structuredKey = row.key;
      this.structuredOpen = true;
    },
    /** dialog เดิมคืนค่าการจับคู่มา — เก็บลง state กลาง แล้วให้ปุ่มสร้างขั้น 5 เป็นคนยิง Execute */
    onStructuredConfigured(payload) {
      const key = this.structuredKey;
      this.structuredOpen = false;

      if (!key || !payload) return;

      this.patch({
        structuredSheets: (this.state.structuredSheets || []).map((row) =>
          row.key === key
            ? {
                ...row,
                sheetName: payload.sheetName || row.sheetName,
                mapping: payload.mapping,
                matchStrategy: payload.matchStrategy,
                onMissing: payload.onMissing,
              }
            : row
        ),
      });
    },

    // ── โหมด Folder (6-A) ────────────────────────────────────────────────────
    /**
     * ค่าเริ่มต้นของโฟลเดอร์ตามความลึก (FOLDER_DEPTH_DEFAULTS · nexa index.html:19147)
     * ชื่อพวกนี้อยู่ใน union ของตัวเลือกเสมอ จึงตั้งตรง ๆ ได้ · ลึกเกินตาราง = ว่าง (ไม่สร้างโหนด)
     */
    defaultFolderType(depth) {
      return CONST_FOLDER_DEPTH_DEFAULTS[depth] || '';
    },
    folderTypeOf(row) {
      const map = this.folderTypeByPath;

      return Object.prototype.hasOwnProperty.call(map, row.path) ? map[row.path] : this.defaultFolderType(row.depth);
    },
    /**
     * เขียนค่าเริ่มต้นของโฟลเดอร์ลง state กลางให้ครบทุกแถว เพื่อให้ StartExtract ได้ผังเต็มเสมอ
     * เรียกจาก watcher/callback เท่านั้น ห้ามเรียกระหว่าง render และต้องเงียบเมื่อค่าไม่เปลี่ยน (กัน loop)
     */
    syncFolderDefaults() {
      if (!this.folderMode || !this.folderPaths.length) return;

      const nodeTypes = this.folderPaths.map((row) => ({ path: row.path, nodeType: this.folderTypeOf(row) }));
      const byLevel = this.depthRows.map((row) => ({ depth: row.depth, relation: row.value }));
      const byPath = this.subFolderRows.map((row) => ({ path: row.path, relation: row.relation }));

      this.patchFolder(nodeTypes, byLevel, byPath);
    },
    /** patch เฉพาะตอนค่าต่างจริง — watcher ที่วนกลับมาเองคือจุดที่ทำให้ขั้นนี้ค้างได้ง่ายที่สุด */
    patchFolder(nodeTypes, byLevel, byPath) {
      const before = JSON.stringify([this.state.folderNodeTypes || [], this.state.folderRelationsByLevel || [], this.state.folderRelations || []]);
      const after = JSON.stringify([nodeTypes, byLevel, byPath]);

      if (before === after) return;

      this.patch({ folderNodeTypes: nodeTypes, folderRelationsByLevel: byLevel, folderRelations: byPath });
    },
    /** ผู้ใช้เลือกเอง = ค่าของผู้ใช้ชนะ และ ✨ ของแถวนั้นต้องหายทันที */
    onFolderTypeChange(path, typeId) {
      const nodeTypes = this.folderRows.map((row) => ({ path: row.path, nodeType: row.path === path ? typeId || '' : row.value }));

      this.aiFilledFolders = this.aiFilledFolders.filter((one) => one !== path);
      this.patchFolder(
        nodeTypes,
        this.depthRows.map((row) => ({ depth: row.depth, relation: row.value })),
        this.subFolderRows.map((row) => ({ path: row.path, relation: row.relation }))
      );
    },
    onDepthRelationChange(depth, relation) {
      const byLevel = this.depthRows.map((row) => ({ depth: row.depth, relation: row.depth === depth ? relation || this.defaultRelation : row.value }));

      this.patchFolder(
        this.folderRows.map((row) => ({ path: row.path, nodeType: row.value })),
        byLevel,
        this.subFolderRows.map((row) => ({ path: row.path, relation: row.relation }))
      );
    },
    onPathRelationChange(path, relation) {
      const byPath = this.subFolderRows.map((row) => ({ path: row.path, relation: row.path === path ? relation || this.defaultRelation : row.relation }));

      this.patchFolder(
        this.folderRows.map((row) => ({ path: row.path, nodeType: row.value })),
        this.depthRows.map((row) => ({ depth: row.depth, relation: row.value })),
        byPath
      );
    },
    setRelationMode(mode) {
      if (!this.canEdit || this.relationMode === mode) return;

      this.patch({ folderRelationMode: mode });
      // แถวของอีกโหมดยังไม่เคยถูกเติม — เติมให้ครบหลัง state เปลี่ยนแล้ว
      this.$nextTick(this.syncFolderDefaults);
    },
    /** โฟลเดอร์ที่มีค่าอยู่แล้วและไม่ได้มาจาก AI หรือค่าเริ่มต้น = ผู้ใช้ตั้งเอง ห้าม AI ทับ */
    manualFolderPaths() {
      return this.folderPaths
        .filter((row) => {
          const current = this.folderTypeOf(row);

          return !!current && current !== this.defaultFolderType(row.depth) && this.aiFilledFolders.indexOf(row.path) < 0;
        })
        .map((row) => row.path);
    },
    /** พอร์ตครึ่งโฟลเดอร์ของ suggestFolderTypes — คืนจำนวนช่องที่ AI เติมให้จริง */
    applyFolderSuggestion(data) {
      if (!this.folderMode || !this.folderPaths.length) return 0;

      const known = this.selectableTypeIds;
      const validRelation = {};
      this.relationItems.forEach((item) => {
        validRelation[item.typeId] = true;
      });

      const typeByPath = {};
      const relationByPath = {};
      const relationByDepth = {};

      (data.folders || []).forEach((item) => {
        if (!item || !item.path) return;
        // ค่าที่ไม่มีในลิสต์ตัวเลือกจริง ห้ามเติมลงช่อง ไม่งั้น v-select จะโชว์ค่าที่เลือกกลับไม่ได้
        if (item.suggestedNodeType && known[item.suggestedNodeType]) typeByPath[item.path] = item.suggestedNodeType;
        if (item.suggestedRelation && validRelation[item.suggestedRelation]) relationByPath[item.path] = item.suggestedRelation;
      });

      (data.relationsByLevel || []).forEach((item) => {
        if (item && typeof item.depth === 'number' && item.relation && validRelation[item.relation]) relationByDepth[item.depth] = item.relation;
      });

      const manual = this.manualFolderPaths();
      const aiFilled = [];

      const nodeTypes = this.folderRows.map((row) => {
        const suggested = typeByPath[row.path];
        if (!suggested || manual.indexOf(row.path) >= 0) return { path: row.path, nodeType: row.value };

        aiFilled.push(row.path);

        return { path: row.path, nodeType: suggested };
      });

      const byLevel = this.depthRows.map((row) => ({ depth: row.depth, relation: relationByDepth[row.depth] || row.value }));
      const byPath = this.subFolderRows.map((row) => ({ path: row.path, relation: relationByPath[row.path] || relationByDepth[row.depth] || row.relation }));

      this.aiFilledFolders = aiFilled;
      this.patchFolder(nodeTypes, byLevel, byPath);

      return aiFilled.length;
    },
  },
};
</script>

<style scoped>
.wiz-section {
  font-size: 15px;
}
.wiz-sub {
  font-size: 12px;
  color: #646464;
}
.wiz-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}
.wiz-actions--stack {
  display: block;
}
.wiz-actions--stack .wiz-action-slot {
  padding-bottom: 8px;
}
.wiz-type-sel {
  display: inline-flex;
  align-items: center;
}
.wiz-type-dot {
  display: inline-block;
  flex-shrink: 0;
  width: 12px;
  height: 12px;
  margin-inline-start: 8px;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.15);
}
.wiz-suggest-status {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #c32e0e;
}
.wiz-level-row {
  padding-top: 4px;
  padding-bottom: 4px;
}
.wiz-level-label {
  font-size: 13px;
}
.wiz-folder-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
}
.wiz-folder-title {
  font-size: 14px;
}
/* ชื่อโฟลเดอร์ยาวได้ไม่จำกัด — ตัดด้วย ellipsis ไม่ใช่ดันแถวจนต้องเลื่อนแนวนอนทั้งหน้า */
.wiz-folder-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
/* ช่องที่ AI เติมให้ = ขอบสีแบรนด์ + ไอคอน ✨ (ห้ามใช้พื้นเหลือง จะชนกับ warning) */
.wiz-ai-filled :deep(.v-field__outline) {
  --v-field-border-width: 2px;
  --v-field-border-opacity: 1;

  color: #c32e0e;
}
</style>
